import { isNil } from '@sixfold/typed-primitives';

import { haversineDistanceKm } from './haversine';
import type {
  Stop,
  StopExternalTimelineEvent,
  StopTimelineEvent,
  TimelineData,
  TourExternalTimelineEvent,
  TourTimelineEvent,
  VehicleBreakRange,
  VehicleLocationChunk,
  VehicleNearStopRange,
  VehiclePing,
  VehicleTimelineData,
  VehicleTrackingRange,
} from './types';
import { VehicleBreakHistory, VehicleHistory } from '../../../vehicle/entities';
import type { StopEvent, Tour, TourEvent, TrackingSegment } from '../../entities';

// Vehicle data input type for the transformer
export interface VehicleTimelineInput {
  vehicleId: string;
  licensePlateNumber: string | null;
  history: VehicleHistory[];
  breakHistory: VehicleBreakHistory[];
}

// Cluster telemetry history into chunks based on gaps
const clusterTelemetryHistory = (
  history: { timestamp: Date }[],
  gapThresholdMinutes: number = 10,
): { from: Date; to: Date }[] => {
  const sortedHistory = [...history].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return sortedHistory.reduce(
    (memo, item) => {
      if (memo.length === 0) {
        return [{ from: item.timestamp, to: item.timestamp }];
      }

      const current = memo[memo.length - 1];
      const diffMinutes = (item.timestamp.getTime() - current.to.getTime()) / (1000 * 60);

      if (diffMinutes < gapThresholdMinutes) {
        current.to = item.timestamp;
      } else {
        memo.push({ from: item.timestamp, to: item.timestamp });
      }

      return memo;
    },
    [] as { from: Date; to: Date }[],
  );
};

// Compute vehicle near stop ranges from history
const computeVehicleNearStopRanges = (
  history: { lat: number; lng: number; timestamp: Date }[],
  stops: { stop_id: number; name: string; lat?: number; lng?: number; radiusMeters?: number }[],
  defaultRadiusMeters: number = 2000,
): VehicleNearStopRange[] => {
  const ranges: VehicleNearStopRange[] = [];

  stops.forEach((stop) => {
    if (stop.lat === undefined || stop.lng === undefined) return;

    const stopLat = stop.lat;
    const stopLng = stop.lng;
    const radius = stop.radiusMeters ?? defaultRadiusMeters;
    const pointsNearStop: { timestamp: Date; stopId: number; stopName: string }[] = [];

    history.forEach((h) => {
      const distance = haversineDistanceKm(h.lat, h.lng, stopLat, stopLng) * 1000;
      if (distance <= radius) {
        pointsNearStop.push({ timestamp: h.timestamp, stopId: stop.stop_id, stopName: stop.name });
      }
    });

    if (pointsNearStop.length === 0) return;

    // Cluster points that are within 30 minutes of each other
    const sortedPoints = pointsNearStop.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // Use reduce to build clusters
    type ClusterType = { from: Date; to: Date; stopId: number; stopName: string };
    const clusters = sortedPoints.reduce<ClusterType[]>((acc, point) => {
      if (acc.length === 0) {
        return [{ from: point.timestamp, to: point.timestamp, stopId: point.stopId, stopName: point.stopName }];
      }

      const lastCluster = acc[acc.length - 1];
      const diffMinutes = (point.timestamp.getTime() - lastCluster.to.getTime()) / (1000 * 60);

      if (diffMinutes < 30) {
        // 30 minute gap threshold for near-stop ranges
        lastCluster.to = point.timestamp;
      } else {
        acc.push({ from: point.timestamp, to: point.timestamp, stopId: point.stopId, stopName: point.stopName });
      }

      return acc;
    }, []);

    // Add all clusters to ranges
    clusters.forEach((cluster) => {
      ranges.push({
        stopId: cluster.stopId,
        stopName: cluster.stopName,
        from: cluster.from.toISOString(),
        to: cluster.to.toISOString(),
      });
    });
  });

  return ranges;
};

const mergeStopRanges = (ranges: VehicleNearStopRange[]): VehicleNearStopRange[] => {
  if (ranges.length === 0) return [];

  const sorted = [...ranges].sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());

  return sorted.reduce<VehicleNearStopRange[]>((acc, current) => {
    if (acc.length === 0) {
      acc.push({ ...current });
      return acc;
    }

    const prev = acc[acc.length - 1];
    const prevEnd = new Date(prev.to).getTime();
    const currentStart = new Date(current.from).getTime();
    const currentEnd = new Date(current.to).getTime();

    // Merge overlapping or back-to-back ranges for the same stop
    if (currentStart <= prevEnd + 1000) {
      if (currentEnd > prevEnd) {
        prev.to = new Date(currentEnd).toISOString();
      }
    } else {
      acc.push({ ...current });
    }

    return acc;
  }, []);
};

const extendNearStopsForLocationGaps = (
  nearStops: VehicleNearStopRange[],
  history: { timestamp: Date }[],
  gapThresholdMinutes: number = 15,
): VehicleNearStopRange[] => {
  if (nearStops.length === 0 || history.length === 0) return nearStops;

  const sortedHistory = [...history].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return nearStops.map((range) => {
    const rangeEndMs = new Date(range.to).getTime();
    const nextPoint = sortedHistory.find((point) => point.timestamp.getTime() > rangeEndMs);

    if (!nextPoint) {
      return range;
    }

    const gapMinutes = (nextPoint.timestamp.getTime() - rangeEndMs) / (1000 * 60);

    if (gapMinutes >= gapThresholdMinutes) {
      return {
        ...range,
        to: nextPoint.timestamp.toISOString(),
      };
    }

    return range;
  });
};

const extendNearStopsWithBreaks = (
  nearStops: VehicleNearStopRange[],
  breaks: VehicleBreakHistory[],
  stops: { stop_id: number; name: string; lat?: number; lng?: number; radiusMeters?: number }[],
  defaultRadiusMeters: number = 2000,
): VehicleNearStopRange[] => {
  if (nearStops.length === 0 || breaks.length === 0) return nearStops;

  const rangesByStop = new Map<number, VehicleNearStopRange[]>();
  nearStops.forEach((range) => {
    const existing = rangesByStop.get(range.stopId) ?? [];
    existing.push({ ...range });
    rangesByStop.set(range.stopId, existing);
  });

  const findStopForTime = (time: Date): { stopId: number; stopName: string } | null => {
    let match: { stopId: number; stopName: string } | null = null;
    rangesByStop.forEach((ranges, stopId) => {
      if (match) return;
      ranges.some((range) => {
        const from = new Date(range.from);
        const to = new Date(range.to);
        if (time >= from && time <= to) {
          match = { stopId, stopName: range.stopName };
          return true;
        }
        return false;
      });
    });
    return match;
  };

  const findStopByCenter = (center?: {
    lat?: number | null;
    lng?: number | null;
  }): { stopId: number; stopName: string } | null => {
    if (isNil(center) || isNil(center.lat) || isNil(center.lng)) {
      return null;
    }

    const centerLat = center.lat;
    const centerLng = center.lng;

    let closest: { stopId: number; stopName: string; distance: number } | null = null;
    stops.forEach((stop) => {
      if (stop.lat == null || stop.lng == null) {
        return;
      }

      const distance = haversineDistanceKm(centerLat, centerLng, stop.lat, stop.lng) * 1000;
      const radius = stop.radiusMeters ?? defaultRadiusMeters;
      if (distance <= radius) {
        if (!closest || distance < closest.distance) {
          closest = { stopId: stop.stop_id, stopName: stop.name, distance };
        }
      }
    });

    if (!closest) {
      return null;
    }

    const { stopId, stopName } = closest;
    return { stopId, stopName };
  };

  breaks.forEach((breakItem) => {
    if (!breakItem.to) return;

    const breakStart = new Date(breakItem.from);
    const breakEnd = new Date(breakItem.to);

    if (breakEnd <= breakStart) return;

    let stopMatch: { stopId: number; stopName: string } | null = findStopByCenter(breakItem.center);

    if (!stopMatch) {
      const startStop = findStopForTime(breakStart);
      const endStop = findStopForTime(breakEnd);

      if (startStop && endStop && startStop.stopId === endStop.stopId) {
        stopMatch = startStop;
      }
    }

    if (!stopMatch) return;
    const { stopId, stopName } = stopMatch;

    const ranges = rangesByStop.get(stopId);
    if (!ranges) return;

    ranges.push({
      stopId,
      stopName,
      from: breakStart.toISOString(),
      to: breakEnd.toISOString(),
    });

    rangesByStop.set(stopId, mergeStopRanges(ranges));
  });

  const stopOrder: number[] = [];
  nearStops.forEach((range) => {
    if (!stopOrder.includes(range.stopId)) {
      stopOrder.push(range.stopId);
    }
  });

  const result: VehicleNearStopRange[] = [];
  stopOrder.forEach((stopId) => {
    const ranges = rangesByStop.get(stopId);
    if (!ranges) return;
    ranges.forEach((range) => result.push(range));
  });

  // Include any stops that might have been added later (shouldn't happen often)
  rangesByStop.forEach((ranges, stopId) => {
    if (!stopOrder.includes(stopId)) {
      ranges.forEach((range) => result.push(range));
    }
  });

  return result;
};

// Transform vehicle data
const transformVehicleData = (
  vehicles: VehicleTimelineInput[],
  trackingSegments: TrackingSegment[],
  stops: {
    stop_id: number;
    location: { name?: string | null; position?: { lat?: number | null; lng?: number | null } | null } | null;
  }[],
  allocationHistory: { vehicleId: string | null; allocatedAt: string | null }[],
): VehicleTimelineData[] => {
  // Build stop info for near-stop calculation
  const stopInfos = stops.map((stop) => ({
    stop_id: stop.stop_id,
    name: stop.location?.name || `Stop ${stop.stop_id}`,
    lat: stop.location?.position?.lat ?? undefined,
    lng: stop.location?.position?.lng ?? undefined,
  }));

  return vehicles.map((vehicle) => {
    // Get tracking ranges for this vehicle
    const vehicleTrackingSegments = trackingSegments.filter((segment) => segment.vehicleId === vehicle.vehicleId);
    const trackingRanges: VehicleTrackingRange[] = vehicleTrackingSegments.map((segment) => ({
      from: segment.trackingStartDate,
      to: segment.trackingEndDate,
    }));

    // Get allocation time for this vehicle
    const allocation = allocationHistory.find((a) => a.vehicleId === vehicle.vehicleId);
    const allocatedAt = allocation?.allocatedAt || null;

    // Parse and compute location chunks
    const parsedHistory = vehicle.history.map((h) => ({
      ...h,
      timestamp: new Date(h.timestamp),
    }));
    const locationChunkDates = clusterTelemetryHistory(parsedHistory);
    const locationChunks: VehicleLocationChunk[] = locationChunkDates.map((chunk) => ({
      from: chunk.from.toISOString(),
      to: chunk.to.toISOString(),
    }));

    // Compute vehicle near stop ranges
    const parsedHistoryWithCoords = parsedHistory.map((h) => ({
      lat: h.lat,
      lng: h.lng,
      timestamp: h.timestamp,
    }));
    const nearStopRangesRaw = computeVehicleNearStopRanges(parsedHistoryWithCoords, stopInfos);
    const nearStopRangesWithGaps = extendNearStopsForLocationGaps(nearStopRangesRaw, parsedHistoryWithCoords);
    const nearStopRanges = extendNearStopsWithBreaks(nearStopRangesWithGaps, vehicle.breakHistory, stopInfos);

    // Transform break history
    const breaks: VehicleBreakRange[] = vehicle.breakHistory.map((b) => ({
      breakId: b.breakId,
      from: String(b.from),
      to: b.to ? String(b.to) : null,
      type: b.type === 'Rest' ? 'rest' : b.type === 'Weekend Break' ? 'weekendBreak' : 'break',
      center: b.center,
    }));

    // Extract individual pings from history
    const pings: VehiclePing[] = parsedHistory.map((h) => ({
      timestamp: h.timestamp.toISOString(),
    }));

    return {
      vehicleId: vehicle.vehicleId,
      licensePlateNumber: vehicle.licensePlateNumber
        ? `${vehicle.licensePlateNumber} (${vehicle.vehicleId})`
        : vehicle.vehicleId,
      allocatedAt,
      trackingRanges,
      nearStopRanges,
      locationChunks,
      breaks,
      pings,
    };
  });
};

/**
 * Transform Tour data from backoffice to TimelineData format
 */
export const transformTourToTimelineData = (
  tour: Tour,
  vehicleInputs: VehicleTimelineInput[] = [],
): TimelineData | null => {
  // Ensure we have stops data
  if (!tour.stops || tour.stops.length === 0) {
    return null;
  }

  // Filter stop events by stop_id
  const stopEventsMap = new Map<number, StopTimelineEvent[]>();
  tour.events.forEach((event) => {
    if (event.__typename === 'StopEvent') {
      const stopEvent = event as StopEvent;
      const stopId = stopEvent.stop_id;
      if (!stopEventsMap.has(stopId)) {
        stopEventsMap.set(stopId, []);
      }
      stopEventsMap.get(stopId)?.push({
        __typename: 'StopEvent',
        event_id: stopEvent.event_id,
        event_name: stopEvent.event_name,
        event_time: stopEvent.event_time,
        event_index: stopEvent.event_index,
        created_at: stopEvent.created_at,
        vehicle_lat: stopEvent.vehicle_lat,
        vehicle_lng: stopEvent.vehicle_lng,
        stop_id: stopEvent.stop_id,
        visibilitySource: stopEvent.visibilitySource
          ? {
              primaryEventSource: stopEvent.visibilitySource.primaryEventSource,
              streamPrecision: stopEvent.visibilitySource.streamPrecision,
            }
          : null,
      });
    }
  });

  // Filter external stop events by stop_id
  const externalStopEventsMap = new Map<number, StopExternalTimelineEvent[]>();
  tour.externalStopEvents.forEach((event) => {
    const stopId = parseInt(event.stopId, 10);
    if (!externalStopEventsMap.has(stopId)) {
      externalStopEventsMap.set(stopId, []);
    }
    externalStopEventsMap.get(stopId)?.push({
      __typename: 'ExternalTourStopEvent',
      eventId: event.eventId,
      timestamp: event.timestamp,
      createdAt: event.createdAt,
      eventSource: event.eventSource,
      qualifier: event.qualifier,
      latitude: event.latitude,
      longitude: event.longitude,
      stopId: event.stopId,
    });
  });

  // Transform stops to timeline format
  const stops: Stop[] = tour.stops.map((stop) => {
    // Determine stop type - normalize to the expected format
    let stopType: 'loading' | 'unloading' | 'waypoint' | 'other' = 'other';
    if (stop.type) {
      const normalizedType = stop.type.toLowerCase();
      if (normalizedType === 'loading' || normalizedType === 'load') {
        stopType = 'loading';
      } else if (normalizedType === 'unloading' || normalizedType === 'unload') {
        stopType = 'unloading';
      } else if (normalizedType === 'waypoint') {
        stopType = 'waypoint';
      }
    }

    return {
      stop_id: stop.stop_id,
      location: {
        name: stop.location?.name || 'Unknown Location',
        address: {
          full_address: stop.location?.address?.full_address || '',
        },
        position:
          stop.location?.position?.lat && stop.location?.position?.lng
            ? {
                lat: stop.location.position.lat,
                lng: stop.location.position.lng,
              }
            : undefined,
      },
      type: stopType,
      status: stop.status || '',
      timeslot:
        stop.timeslot && stop.timeslot.begin && stop.timeslot.end && stop.timeslot.timezone
          ? {
              begin: stop.timeslot.begin,
              end: stop.timeslot.end,
              timezone: stop.timeslot.timezone,
            }
          : undefined,
      arrival_time: stop.arrival_time || undefined,
      departure_time: stop.departure_time || undefined,
      display_sequence_number: stop.sequence_number,
      platform_sequence_number: stop.platformSequenceNumber ?? undefined,
      events: stopEventsMap.get(stop.stop_id) || [],
      externalEvents: externalStopEventsMap.get(stop.stop_id) || [],
    };
  });

  // We should have stops since we checked at the beginning
  if (stops.length === 0) {
    return null;
  }

  // Transform tour events
  const tourEvents: TourTimelineEvent[] = tour.events
    .filter((event) => event.__typename === 'TourEvent')
    .map((event) => {
      const tourEvent = event as TourEvent;
      return {
        __typename: 'TourEvent' as const,
        event_id: tourEvent.event_id,
        event_name: tourEvent.event_name,
        event_time: tourEvent.event_time,
        event_index: tourEvent.event_index,
        created_at: tourEvent.created_at,
        vehicle_lat: tourEvent.vehicle_lat,
        vehicle_lng: tourEvent.vehicle_lng,
        visibilitySource: tourEvent.visibilitySource
          ? {
              primaryEventSource: tourEvent.visibilitySource.primaryEventSource,
              streamPrecision: tourEvent.visibilitySource.streamPrecision,
            }
          : null,
      };
    });

  // Transform external tour events
  const tourExternalEvents: TourExternalTimelineEvent[] = tour.externalEvents.map((event) => ({
    __typename: 'ExternalTourEvent' as const,
    eventId: event.eventId,
    timestamp: event.timestamp,
    createdAt: event.createdAt,
    eventSource: event.eventSource,
    qualifier: event.qualifier,
    latitude: event.latitude,
    longitude: event.longitude,
  }));

  // Transform vehicle data
  const vehicles = transformVehicleData(
    vehicleInputs,
    tour.trackingSegments,
    tour.stops,
    tour.vehicleAllocationHistory,
  );

  return {
    tour_id: tour.tour_id,
    start_time: tour.start_time || '',
    visibility_start_time: tour.visibility_start_time || '',
    visibility_end_time: tour.visibility_end_time || '',
    stops,
    tourEvents,
    tourExternalEvents,
    vehicles,
  };
};
